import { ThemeService } from '@wind-log_library/fe-component-library';
import moment from 'moment';
import { DEVICE_TYPES, ISeries } from '../interfaces/data-studio.interface';
import { SeriesTableType } from '../components/data-analysis/time-series/series-table/series-table.component';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import { createEnumFromValues } from 'app/shared/helper/common.helper';

export const DEFAULT_START_DATE_TS = 1690855800000;
export const DEFAULT_END_DATE_TS = 1691459400000;
export const MIN_DATE = moment().subtract(4, 'month').valueOf();

export const textStyle = {
  style: {
    color: 'var(--font)'
  }
}

export const chartTypesOptions = [
  {
    type: 'area',
    marker: {
      symbol: null
    },
    dashStyle: null
  },
  {
    type: 'areaspline',
    marker: {
      symbol: null
    },
    dashStyle: null
  },
  {
    type: 'scatter',
    marker: {
      symbol: 'circle'
    },
    dashStyle: null
  },
  {
    type: 'column',
    marker: {
      symbol: null
    },
    dashStyle: null
  },
  {
    type: 'spline',
    marker: {
      symbol: null
    },
    dashStyle: 'ShortDash'
  },
  {
    type: 'scatter',
    marker: {
      symbol: 'diamond'
    },
    dashStyle: null
  },
  {
    type: 'spline',
    marker: {
      symbol: null
    },
    dashStyle: 'ShortDot'
  },
  {
    type: 'line',
    marker: {
      symbol: null
    },
    dashStyle: null
  },
  {
    type: 'line',
    marker: {
      symbol: null
    },
    dashStyle: null,
    step: true
  },
  {
    type: 'scatter',
    marker: {
      symbol: 'triangle'
    },
    dashStyle: null
  }
]

export const HIGHCHART_OPTIONS_DEFAULT: Highcharts.Options = {
  title: {
    ...textStyle,
    text: null
  },
  chart: {
    type: 'line',
    zooming: {
      type: 'x'
    },
    animation: false,
    backgroundColor: 'var(--card)'
  },
  xAxis: {
    tickColor: 'var(--font)',
    type: 'datetime',
    lineColor: 'var(--divider)',
    dateTimeLabelFormats: {
      day: '%d %b %y',
    },
    labels: { ...textStyle }
  },
  yAxis: {
    tickColor: 'var(--font)',
    gridLineColor: 'var(--chart-bg-default-color)',
    labels: { ...textStyle },
    title: { ...textStyle }
  },
  legend: {
    title: {
      ...textStyle
    },
    itemStyle: {
      color: 'var(--font)'
    },
    itemHoverStyle: {
      color: 'var(--primary)'
    },
    verticalAlign: 'top',
    labelFormatter: function () {
      const element: any = this;
      return element?.userOptions?.assetDisplayName + ' | ' + element?.name;
    }
  },
  tooltip: {
    shared: true,
    xDateFormat: '%d %b %y %H %M',
    backgroundColor: 'var(--card)',
    formatter: function () {
      const element: any = this;
      if (this.series.userOptions.type === 'scatter') {
        return `
            <span class="font-size-very-small" style="color:${element.color}"> ● </span> 
            <span class="font-size-very-small">${element?.series?.userOptions?.assetDisplayName + ' | ' + element.series.name}</span><br/>
            <br/>${element.series.userOptions.unitY} ${element.series.userOptions.unitY === element.series.userOptions.unit ? '[Y-axis]' : ''}: <b>${Number(element.y).toFixed(signalValueFormatter(element.series.userOptions.signalY))}</b>
            <br/>${element.series.userOptions.unit} ${element.series.userOptions.unitY === element.series.userOptions.unit ? '[X-axis]' : ''}: <b>${Number(element.x).toFixed(signalValueFormatter(element.series.userOptions.signal))}</b>
          `
      } else {
        return this.points.reduce(function (s, point: any) {
          return s + '<br/>' + `<span style="color:${point.color}"> ● </span>` + point?.series?.userOptions?.assetDisplayName + ' | ' + point.series.name + ': ' +
            '<b>' + Number(point.y).toFixed(signalValueFormatter(point?.series?.userOptions?.id)) + '</b>';
        }, '<div class="font-size-very-small">' + moment(this.x).format('DD MMM YY HH:mm') + '</div>');
      }
    },
    style: {
      color: 'var(--primary)'
    }
  },
  credits: { enabled: false },
  exporting: {
    enabled: false,
    sourceWidth: 2056,
    sourceHeight: 400,
    fallbackToExportServer: false,
    buttons: {
      contextButton: {
        menuItems: ['downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadXLS'],
        symbol: '',
        useHTML: true,
        theme: {
          fill: 'var(--background)'
        },
        text: `
          <i class="far fa-download font-icons pointer mr-2 text-primary"></i>
          <span class="pointer text-primary" > Export </span>
          <i class="fas cursor-pointer text-primary ml-2 font-size-small fa-chevron-down"></i>
        `
      }
    }
  },
  time: {
    timezone: undefined
  },
  series: []
}

export function chartExportingOptions(chartData, themeService: ThemeService) {
  const card = themeService.getColor('--card');
  const divider = themeService.getColor('--divider');
  const font = themeService.getColor('--font');

  const yAxisConfig = {
    tickColor: divider,
    lineColor: divider,
    gridLineColor: divider,
    labels: { style: { color: font } },
    title: { style: { color: font } }
  }
  return {
    chart: {
      backgroundColor: card
    },
    xAxis: {
      tickColor: divider,
      lineColor: divider,
      labels: { style: { color: font } }
    },
    yAxis: chartData.yAxis.map(d => yAxisConfig),
    legend: { itemStyle: { color: font } }
  }
}

export const GRANULARITY_OPTIONS = {
  names: ['10 min', '1 hour', '1 day', '30 days', '1 year'],
  values: ['10m', '1h', '1d', '30d', '1y']
} as const;

export type GranularityType = typeof GRANULARITY_OPTIONS.values[number];

export const GRANULARITIES: { [K in GranularityType]: K } = createEnumFromValues([...GRANULARITY_OPTIONS.values, '30s'] as const);

export const PERIOD_OPTIONS = {
  names: ['Day', 'Week', 'Month', 'Year', 'Custom'],
  values: ['day', 'week', 'month', 'year', 'custom']
};

export const periodInputFormats = {
  month: 'MMMM YYYY',
  year: 'YYYY',
  day: 'DD MMM YYYY'
}

export const SIGNALS_TYPES_LIST = [
  {
    title: 'electrical',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.ELECTRICAL',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.ELECTRICAL',
    checked: true,
    databaseId: 2
  },
  {
    title: 'ambient',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.AMBIENT',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.AMBIENT',
    checked: true,
    databaseId: 1
  },
  {
    title: 'technical',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.TECHNICAL',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.TECHNICAL',
    checked: true,
    databaseId: 3
  },
  {
    title: 'temperature',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.TEMPERATURE',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.TEMPERATURE',
    checked: true,
    databaseId: 4
  },
  {
    title: 'parkProduction',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.PARK_PRODUCTION',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.PARK_PRODUCTION',
    checked: true,
    databaseId: 5
  },
  {
    title: 'marketPrice',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.MARKET_PRICE',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.MARKET_PRICE',
    checked: true,
    databaseId: 6
  },
  {
    title: 'controlSystem',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.CONTROL_SYSTEM',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.CONTROL_SYSTEM',
    checked: true,
    databaseId: 7
  },
  {
    title: 'metmastSensor',
    titleTranslationKey: 'DATA_STUDIO.SIGNALS.GROUPS.METMAST',
    translationKey: 'DATA_STUDIO.SIGNALS.GROUPS.METMAST',
    checked: true,
    databaseId: 8
  }
];

export const DEVICE_TYPES_LIST = [
  {
    title: 'Parks',
    type: 'park',
    titleTranslationKey: 'COMMON.PARKS',
    titleTranslationKeySingular: 'COMMON.PARK',
    checked: true
  },
  {
    title: 'Turbines',
    type: 'turbine',
    titleTranslationKey: 'COMMON.TURBINES',
    titleTranslationKeySingular: 'COMMON.TURBINE',
    checked: true
  },
  {
    title: 'ControlSystems',
    type: 'controlSystem',
    titleTranslationKey: 'COMMON.CONTROL_SYSTEMS',
    titleTranslationKeySingular: 'COMMON.CONTROL_SYSTEM',
    checked: true
  },
  {
    title: 'Metmasts',
    type: 'metmastSensor',
    titleTranslationKey: 'COMMON.METMASTS',
    titleTranslationKeySingular: 'COMMON.METMASTS',
    checked: true
  }
];

export const newQueryDefaultParams = {
  name: 'New Query',
  granularity: '1h',
  period: 'Day',
  startTs: moment().startOf('day').subtract(1, 'day').valueOf(),
  endTs: moment().endOf('day').subtract(1, 'day').valueOf(),
  series: []
}

export const QUERY_VIEW_PAGES = ['TIMESERIES', 'SCATTER_PLOT', 'POWER_CURVE', 'ALL_QUERIES_LIST'];

export function findCommonSignals(devicesList) {
  // Flatten all signalsList into a single array
  const allProps = devicesList.flatMap(item => item.signalsList);

  // Use a Map to count occurrences of each prop
  const propCounts = new Map();
  allProps.forEach(prop => {
    const key = JSON.stringify(prop); // Convert prop object to a string for Map keys
    propCounts.set(key, (propCounts.get(key) || 0) + 1);
  });

  // Filter signalsList that occur in all items
  const commonProps = Array.from(propCounts.entries())
    .filter(([key, count]) => count === devicesList.length)
    .map(([key]) => JSON.parse(key)); // Convert string back to object

  return commonProps;
}

export function generateTimestamps(startDateTs, endDateTs) {
  const intervalDuration = 10 * 60 * 1000; // 10 minutes in milliseconds

  const numberOfIntervals = Math.floor((endDateTs - startDateTs) / intervalDuration) + 1;

  return Array.from({ length: numberOfIntervals }, (_, index) =>
    startDateTs + index * intervalDuration
  );
}

export function getSeriesTableData(series: ISeries[], type: SeriesTableType, dp: DateTimeFormatPreferencePipe, timezone: string) {
  switch (type) {
    case 'scatter-plot': return { ...getTableDataScatterPlot(series, dp, timezone), ...getSeriesTableHeaders(series, true) };
    case 'time-series': return { ...getTableDataTimeSeries(series, dp, timezone), ...getSeriesTableHeaders(series) };
  }
}
export function getSeriesTableHeaders(series: ISeries[], isScatterPlot = false) {
  const titles = [];
  const titlesNames = [];
  const originalTitles = [];

  series.forEach(serie => {
    const name = serie.device?.replace(/_/g, ' ');
    const nameY = serie.deviceY?.replace(/_/g, ' ');
    if (isScatterPlot) {
      let serieName = `${(name || '')} | ${serie.signalName} [${serie.unit}]`;
      originalTitles.push(serieName);
      if (originalTitles.find(t => t === serieName)) {
        const sameTitleCount = originalTitles.filter(t => t === serieName).length;
        serieName += sameTitleCount !== 1 ? ` (${sameTitleCount - 1})` : '';
      }

      titles.push(serieName);
      titlesNames.push(`${serie.parkDisplayName} | ${serieName}`);

      let serieNameY = `${nameY || ''} | ${serie.signalNameY} [${serie.unitY}]`;
      originalTitles.push(serieNameY);
      if (originalTitles.find(t => t === serieNameY)) {
        const sameTitleCount = originalTitles.filter(t => t === serieNameY).length;
        serieNameY += sameTitleCount !== 1 ? ` (${sameTitleCount - 1})` : '';
      }
      titles.push(serieNameY);
      titlesNames.push(`${serie.parkDisplayName} | ${serieNameY}`);
    } else {
      const signalNameWithUnit = `${name || serie.parkDisplayName} | ${serie.signalName} [${serie.unit}]`;
      const id = serie.deviceType === DEVICE_TYPES.METMAST ? `${serie.device.replace(/ /g, '_')}_${serie.id}` : serie.id;
      titles.push(id);
      titlesNames.push(signalNameWithUnit);
    }
  });

  titles.unshift('time')
  titlesNames.unshift('Timestamp')

  return { titles, titlesNames };
}

export function getTableDataScatterPlot(series: ISeries[], dp: DateTimeFormatPreferencePipe, timezone?: string) {
  const gmtTimezone = TimezoneService.ianaToGMT(timezone);

  let data = [];
  const originalTitles = [];
  series.forEach(s => {
    let serieName = `${(s?.device?.replace(/_/g, ' ') || '')} | ${s.signalName} [${s.unit}]`;
    originalTitles.push(serieName);
    if (originalTitles.find(t => t === serieName)) {
      const sameTitleCount = originalTitles.filter(t => t === serieName).length;
      serieName += sameTitleCount !== 1 ? ` (${sameTitleCount - 1})` : '';
    }

    let serieNameY = `${(s?.deviceY?.replace(/_/g, ' ') || '')} | ${s.signalNameY} [${s.unitY}]`;
    originalTitles.push(serieNameY);
    if (originalTitles.find(t => t === serieNameY)) {
      const sameTitleCount = originalTitles.filter(t => t === serieNameY).length;
      serieNameY += sameTitleCount !== 1 ? ` (${sameTitleCount - 1})` : '';
    }

    s.chartDataX?.forEach((a, i) => {
      data.push({
        timestamp: s.chartDataX[i][0],
        time: dp.transform(s.chartDataX[i][0], 'dmyt'),
        [serieName]: s.chartData[i] ? s.chartData?.[i]?.[0] : '',
        [serieNameY]: s.chartData[i] ? s.chartData?.[i]?.[1] : ''
      })
    })
  })
  const dataJSON = data.sort((a, b) => a.timestamp - b.timestamp)?.map(({ time, ...rest }) => ({
    [`Timestamp (${gmtTimezone})`]: time,
    ...rest
  }));
  return { data: data.sort((a, b) => a.timestamp - b.timestamp), dataJSON };
}

export function getTableDataTimeSeries(series: ISeries[], dp: DateTimeFormatPreferencePipe, timezone: string) {
  const timeEntry = {};
  const mapperJSON = {};
  series.forEach(item => {
    const name = `${item.device?.replace(/_/g, ' ') || item.parkDisplayName} | ${item.signalName} [${item.unit}]`;
    const id = item.deviceType === DEVICE_TYPES.METMAST ? `${item.device.replace(/ /g, '_')}_${item.id}` : item.id;
    timeEntry[id] = new Map();
    mapperJSON[id] = name;
    item.chartData.forEach(dataItem => {
      timeEntry[id].set(...dataItem);
    })
  });

  const merged = mergeData(timeEntry);
  // Sort the keys
  const sortedKeys = Array.from(merged.keys()).sort((a, b) => a - b);

  // Create the final sorted map
  const sortedMerged = new Map(sortedKeys.map(key => [key, merged.get(key)]));

  const gmtTimezone = TimezoneService.ianaToGMT(timezone);

  const timestampColumn = `Timestamp (${gmtTimezone})`;
  let dataJSON = Array.from(sortedMerged).map(([time, values]) => {
    const names = {};
    for (const key in values) {
      names[mapperJSON[key]] = values[key] !== null ? Number(values[key]).toFixed(signalValueFormatter(key)) : null;
    }
    return { [timestampColumn]: dp.transform(time, 'dmyt'), ...names }
  });

  const sortedObjectKeys = dataJSON[0] ? Object.keys(dataJSON[0]).sort((a, b) => b === timestampColumn ? 1 : a.localeCompare(b, undefined, { numeric: true })) : [];
  dataJSON = dataJSON.map(d => {
    const sortedObj = {};
    sortedObjectKeys.forEach(key => sortedObj[key] = d[key]);
    return sortedObj;
  });

  const data = Array.from(sortedMerged).map(([time, values]) => ({ timestamp: time, time: dp.transform(time, 'dmyt'), ...values }));

  return { data, dataJSON }
}


export function mergeArrays(arr1, arr2) {
  const map1 = new Map(arr1);
  const map2 = new Map(arr2);

  const mergedMap = new Map();

  for (const [key, value1] of map1) {
    if (map2.has(key)) {
      if (value1 !== undefined && map2.get(key) !== undefined) {
        mergedMap.set(key, [value1, map2.get(key)]);
      }
    }
  }

  return Array.from(mergedMap.values());
}

function mergeData(timeEntry) {
  const merged = new Map();

  // Create a Set of all unique keys
  const allKeys = new Set();

  for (const submapKey in timeEntry) {
    const submap = timeEntry[submapKey];
    for (const key of submap.keys()) {
      allKeys.add(key);
    }
  }

  for (const key of allKeys) {
    const values = {};

    for (const submapKey in timeEntry) {
      const submap = timeEntry[submapKey];
      const value = submap.get(key);
      values[submapKey] = value !== undefined ? value : null;
    }

    merged.set(key, values);
  }

  return merged;
}

export function signalValueFormatter(signal: string): number {
  let defaultDecimalNumber = 2;
  return defaultDecimalNumber;
}
